import { ApiClient, ApiError, type RequestOf, type ResponseOf } from '@platform/api-client';

/**
 * The platform panel talks to the same API as the seller panel, from its own
 * origin.
 *
 * Nothing here is a seller endpoint. The application is deliberately incapable
 * of reaching one: it holds no product, order or invoice calls at all, so a
 * mistake in this codebase cannot show a customer's catalogue on a staff
 * screen.
 */
export const api = new ApiClient({
    onUnauthenticated: () => {
        window.dispatchEvent(new CustomEvent('session:expired'));
    },
});

export { ApiError };

export type Me = ResponseOf<'/v1/me', 'get'>;
export type LoginBody = RequestOf<'/v1/auth/login', 'post'>;
export type SessionPayload = ResponseOf<'/v1/auth/login', 'post'>;
export type SecondFactorBody = RequestOf<'/v1/auth/login/second-factor', 'post'>;
export type TwoFactorSetup = ResponseOf<'/v1/me/two-factor', 'post'>;
export type TwoFactorConfirmBody = RequestOf<'/v1/me/two-factor/confirm', 'post'>;
export type TwoFactorConfirmation = ResponseOf<'/v1/me/two-factor/confirm', 'post'>;
export type TwoFactorDisableBody = RequestOf<'/v1/me/two-factor/disable', 'post'>;

export type PlatformSummary = ResponseOf<'/v1/admin/summary', 'get'>;
export type TenantList = ResponseOf<'/v1/admin/tenants', 'get'>;
export type Tenant = TenantList['tenants'][number];
export type FailingChannels = ResponseOf<'/v1/admin/failing-channels', 'get'>;
export type FailingChannel = FailingChannels['channels'][number];
export type StaffList = ResponseOf<'/v1/admin/staff', 'get'>;
export type StaffMember = StaffList['staff'][number];
export type GrantStaffBody = RequestOf<'/v1/admin/staff', 'post'>;
export type SupportHandoff = ResponseOf<'/v1/admin/tenants/{tenantId}/impersonate', 'post'>;
export type BillingMetrics = ResponseOf<'/v1/admin/metrics/billing', 'get'>;
export type PlanStat = BillingMetrics['plans'][number];
export type TenantFeatures = ResponseOf<'/v1/admin/tenants/{tenantId}/features', 'get'>;
export type TenantFeature = TenantFeatures['features'][number];
export type SetFeatureBody = RequestOf<'/v1/admin/tenants/{tenantId}/features', 'put'>;
/** A feature's level (ADR 0015), as the contract names them. */
export type FeatureLevel = NonNullable<TenantFeature['level']>;
export type SetPlanBody = RequestOf<'/v1/admin/tenants/{tenantId}/plan', 'put'>;
export type Announcements = ResponseOf<'/v1/admin/announcements', 'get'>;
export type Announcement = Announcements['announcements'][number];
export type CreateAnnouncementBody = RequestOf<'/v1/admin/announcements', 'post'>;

export type TenantDetail = ResponseOf<'/v1/admin/tenants/{tenantId}', 'get'>;
export type TenantUsers = ResponseOf<'/v1/admin/tenants/{tenantId}/users', 'get'>;
export type TenantUser = TenantUsers['users'][number];
export type TenantChannels = ResponseOf<'/v1/admin/tenants/{tenantId}/channels', 'get'>;
export type TenantChannel = TenantChannels['channels'][number];
export type TenantHandoffs = ResponseOf<'/v1/admin/tenants/{tenantId}/handoffs', 'get'>;
export type Handoff = TenantHandoffs['handoffs'][number];
export type TenantStatus = 'trial' | 'active' | 'suspended' | 'cancelled';
export type UserList = ResponseOf<'/v1/admin/users', 'get'>;
export type PlatformUser = UserList['users'][number];
export type AuditList = ResponseOf<'/v1/admin/audit', 'get'>;
export type AuditEntry = AuditList['entries'][number];
export type Ops = ResponseOf<'/v1/admin/ops', 'get'>;
export type Plans = ResponseOf<'/v1/admin/plans', 'get'>;
export type Plan = Plans['plans'][number];
export type UpdatePlanBody = RequestOf<'/v1/admin/plans/{key}', 'put'>;
export type CreatePlanBody = RequestOf<'/v1/admin/plans', 'post'>;
export type RequestList = ResponseOf<'/v1/admin/requests', 'get'>;
export type PlatformRequest = RequestList['requests'][number];
export type UpdateRequestBody = RequestOf<'/v1/admin/requests/{id}', 'put'>;
export type BillingCharges = ResponseOf<'/v1/admin/billing/charges', 'get'>;
export type BillingCharge = BillingCharges['charges'][number];
export type ChargeStatus = BillingCharge['status'];
export type BankDetails = ResponseOf<'/v1/admin/billing/settings', 'get'>;
export type SetBankDetailsBody = RequestOf<'/v1/admin/billing/settings', 'put'>;
export type PaymentProviders = ResponseOf<'/v1/admin/billing/payment-providers', 'get'>;
export type PaymentProvider = PaymentProviders['providers'][number];
export type PaymentAccounts = ResponseOf<'/v1/admin/billing/payment-accounts', 'get'>;
export type PaymentAccount = PaymentAccounts['accounts'][number];
export type ConnectPaymentBody = RequestOf<'/v1/admin/billing/payment-accounts', 'post'>;
export type SetCardServiceBody = RequestOf<'/v1/admin/billing/card-service', 'put'>;
export type InvoiceSettings = ResponseOf<'/v1/admin/billing/invoice-settings', 'get'>;
export type InvoiceIssuer = InvoiceSettings['issuer'];
export type InvoiceProviders = ResponseOf<'/v1/admin/billing/invoice-providers', 'get'>;
export type InvoiceProvider = InvoiceProviders['providers'][number];
export type InvoiceAccounts = ResponseOf<'/v1/admin/billing/invoice-accounts', 'get'>;
export type InvoiceAccount = InvoiceAccounts['accounts'][number];
export type ConnectInvoiceBody = RequestOf<'/v1/admin/billing/invoice-accounts', 'post'>;
export type InvoiceOffers = ResponseOf<'/v1/admin/invoice-offers', 'get'>;
export type InvoiceOffer = InvoiceOffers['offers'][number];
export type SetInvoiceServiceBody = RequestOf<'/v1/admin/billing/invoice-service', 'put'>;
export type IssuedOpenInvoices = ResponseOf<'/v1/admin/billing/invoices/issue-open', 'post'>;
export type ChargeInvoiceLink = ResponseOf<'/v1/admin/billing/charges/{chargeId}/invoice', 'get'>;
/** What a charge was bought on, with the evidence kept beside it (ADR 0018 §5). */
export type ChargeAgreement = ResponseOf<'/v1/admin/billing/charges/{chargeId}/agreement', 'get'>;
/** An account's acceptances of the terms (ADR 0018 §1). */
export type TermsAcceptances = ResponseOf<'/v1/admin/tenants/{tenantId}/terms-acceptances', 'get'>;
export type TermsAcceptance = TermsAcceptances['acceptances'][number];
export type TenantUsage = ResponseOf<'/v1/admin/tenants/{tenantId}/usage', 'get'>;
/** One limit a package sets, beside what an account uses of it (ADR 0017 §1). */
export type LimitState = TenantUsage['limitStates'][number];
export type LimitResource = LimitState['resource'];
export type PlanFit = TenantUsage['plans'][number];
export type TrialExtensionBody = RequestOf<'/v1/admin/tenants/{tenantId}/trial-extension', 'post'>;
export type TrialExtension = ResponseOf<'/v1/admin/tenants/{tenantId}/trial-extension', 'post'>;
export type OverLimitAccounts = ResponseOf<'/v1/admin/accounts/over-limit', 'get'>;
export type OverLimitAccount = OverLimitAccounts['accounts'][number];
export type LapsedAccounts = ResponseOf<'/v1/admin/accounts/lapsed', 'get'>;
export type LapsedAccount = LapsedAccounts['accounts'][number];
export type PlanPreviewBody = RequestOf<'/v1/admin/plans/preview', 'post'>;
export type PlanPreview = ResponseOf<'/v1/admin/plans/preview', 'post'>;
export type PreviewPrice = PlanPreview['monthly'];
export type PriceBasis = ResponseOf<'/v1/admin/billing/price-basis', 'get'>;
/** Whether a package's listed price includes VAT (ADR 0017 §10). */
export type VatBasis = PriceBasis['basis'];

/** Builds a query string, leaving out empty values so the URL stays readable. */
export function query(params: Record<string, string | number | string[] | undefined>): string {
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === '') continue;
        // A list repeats its key, the form the API reads a list in.
        if (Array.isArray(value)) {
            for (const item of value) search.append(key, item);
            continue;
        }
        search.set(key, String(value));
    }
    const encoded = search.toString();
    return encoded ? `?${encoded}` : '';
}

export const endpoints = {
    me: () => api.get<Me>('/v1/me'),
    login: (body: LoginBody) => api.post<SessionPayload>('/v1/auth/login', body),
    logout: () => api.post<{ ok: boolean }>('/v1/auth/logout'),
    // Two-step sign-in, optional for staff as for everyone (docs/07 §4).
    completeSecondFactor: (body: SecondFactorBody) => api.post<SessionPayload>('/v1/auth/login/second-factor', body),
    beginTwoFactor: () => api.post<TwoFactorSetup>('/v1/me/two-factor'),
    confirmTwoFactor: (body: TwoFactorConfirmBody) => api.post<TwoFactorConfirmation>('/v1/me/two-factor/confirm', body),
    disableTwoFactor: (body: TwoFactorDisableBody) => api.post<{ ok: boolean }>('/v1/me/two-factor/disable', body),

    summary: () => api.get<PlatformSummary>('/v1/admin/summary'),
    tenants: (params: { search?: string; status?: string; limit?: number; offset?: number }) =>
        api.get<TenantList>(`/v1/admin/tenants${query(params)}`),
    tenantDetail: (tenantId: string) => api.get<TenantDetail>(`/v1/admin/tenants/${tenantId}`),
    tenantUsers: (tenantId: string) => api.get<TenantUsers>(`/v1/admin/tenants/${tenantId}/users`),
    tenantTermsAcceptances: (tenantId: string) => api.get<TermsAcceptances>(`/v1/admin/tenants/${tenantId}/terms-acceptances`),
    tenantChannels: (tenantId: string) => api.get<TenantChannels>(`/v1/admin/tenants/${tenantId}/channels`),
    tenantHandoffs: (tenantId: string) => api.get<TenantHandoffs>(`/v1/admin/tenants/${tenantId}/handoffs`),
    setTenantStatus: (tenantId: string, status: Exclude<TenantStatus, 'trial'>) =>
        api.put<{ status: string }>(`/v1/admin/tenants/${tenantId}/status`, {
            status,
        }),

    users: (params: { search?: string; status?: string; limit?: number; offset?: number }) => api.get<UserList>(`/v1/admin/users${query(params)}`),
    setUserStatus: (userId: string, status: 'active' | 'disabled') => api.put<{ status: string }>(`/v1/admin/users/${userId}/status`, { status }),
    resetUserTwoFactor: (userId: string) => api.post<{ status: string }>(`/v1/admin/users/${userId}/two-factor/reset`),

    audit: (params: { tenantId?: string; search?: string; actions?: string[]; limit?: number; offset?: number }) =>
        api.get<AuditList>(`/v1/admin/audit${query(params)}`),
    ops: () => api.get<Ops>('/v1/admin/ops'),
    plans: () => api.get<Plans>('/v1/admin/plans'),
    updatePlan: (key: string, body: UpdatePlanBody) => api.put<Plans>(`/v1/admin/plans/${key}`, body),
    createPlan: (body: CreatePlanBody) => api.post<Plans>('/v1/admin/plans', body),
    requests: (params: { status?: string; kind?: string; limit?: number; offset?: number }) =>
        api.get<RequestList>(`/v1/admin/requests${query(params)}`),
    openRequests: () => api.get<{ open: number }>('/v1/admin/requests/open-count'),
    updateRequest: (id: string, body: UpdateRequestBody) => api.put<{ ok: boolean }>(`/v1/admin/requests/${id}`, body),
    failingChannels: () => api.get<FailingChannels>('/v1/admin/failing-channels'),

    staff: () => api.get<StaffList>('/v1/admin/staff'),
    grantStaff: (body: GrantStaffBody) => api.post<StaffMember>('/v1/admin/staff', body),
    revokeStaff: (userId: string) => api.delete<{ ok: boolean }>(`/v1/admin/staff/${userId}`),

    // Returns a single-use link into the seller panel rather than a session here.
    // This origin never holds a cookie that is valid inside a customer account.
    supportHandoff: (tenantId: string) => api.post<SupportHandoff>(`/v1/admin/tenants/${tenantId}/impersonate`),

    billingMetrics: () => api.get<BillingMetrics>('/v1/admin/metrics/billing'),
    tenantFeatures: (tenantId: string) => api.get<TenantFeatures>(`/v1/admin/tenants/${tenantId}/features`),
    setTenantFeature: (tenantId: string, body: SetFeatureBody) => api.put<TenantFeatures>(`/v1/admin/tenants/${tenantId}/features`, body),
    clearTenantFeature: (tenantId: string, feature: string) =>
        api.delete<TenantFeatures>(`/v1/admin/tenants/${tenantId}/features/${encodeURIComponent(feature)}`),
    setTenantPlan: (tenantId: string, body: SetPlanBody) => api.put<{ ok: boolean }>(`/v1/admin/tenants/${tenantId}/plan`, body),
    tenantUsage: (tenantId: string) => api.get<TenantUsage>(`/v1/admin/tenants/${tenantId}/usage`),
    extendTrial: (tenantId: string, body: TrialExtensionBody) => api.post<TrialExtension>(`/v1/admin/tenants/${tenantId}/trial-extension`, body),
    overLimitAccounts: () => api.get<OverLimitAccounts>('/v1/admin/accounts/over-limit'),
    lapsedAccounts: () => api.get<LapsedAccounts>('/v1/admin/accounts/lapsed'),
    // A card as it is being typed; nothing is saved, so it is read like a query.
    previewPlan: (body: PlanPreviewBody) => api.post<PlanPreview>('/v1/admin/plans/preview', body),
    priceBasis: () => api.get<PriceBasis>('/v1/admin/billing/price-basis'),
    setPriceBasis: (basis: VatBasis) => api.put<PriceBasis>('/v1/admin/billing/price-basis', { basis }),

    charges: (params: { status?: string; limit?: number; offset?: number }) => api.get<BillingCharges>(`/v1/admin/billing/charges${query(params)}`),
    approveCharge: (chargeId: string) => api.post<void>(`/v1/admin/billing/charges/${chargeId}/approve`),
    applyCharge: (chargeId: string) => api.post<void>(`/v1/admin/billing/charges/${chargeId}/apply`),
    cancelCharge: (chargeId: string, note: string) => api.post<void>(`/v1/admin/billing/charges/${chargeId}/cancel`, { note }),
    refundCharge: (chargeId: string, note: string) => api.post<void>(`/v1/admin/billing/charges/${chargeId}/refund`, { note }),
    bankDetails: () => api.get<BankDetails>('/v1/admin/billing/settings'),
    setBankDetails: (body: SetBankDetailsBody) => api.put<BankDetails>('/v1/admin/billing/settings', body),
    paymentProviders: () => api.get<PaymentProviders>('/v1/admin/billing/payment-providers'),
    paymentAccounts: () => api.get<PaymentAccounts>('/v1/admin/billing/payment-accounts'),
    connectPayment: (body: ConnectPaymentBody) => api.post<PaymentAccount>('/v1/admin/billing/payment-accounts', body),
    verifyPayment: (accountId: string) => api.post<PaymentAccount>(`/v1/admin/billing/payment-accounts/${accountId}/verify`),
    withdrawPayment: (accountId: string) => api.delete<void>(`/v1/admin/billing/payment-accounts/${accountId}`),
    setCardService: (body: SetCardServiceBody) => api.put<void>('/v1/admin/billing/card-service', body),
    invoiceSettings: () => api.get<InvoiceSettings>('/v1/admin/billing/invoice-settings'),
    setInvoiceIssuer: (body: InvoiceIssuer) => api.put<InvoiceSettings>('/v1/admin/billing/invoice-settings', body),
    invoiceProviders: () => api.get<InvoiceProviders>('/v1/admin/billing/invoice-providers'),
    invoiceAccounts: () => api.get<InvoiceAccounts>('/v1/admin/billing/invoice-accounts'),
    connectInvoice: (body: ConnectInvoiceBody) => api.post<InvoiceAccount>('/v1/admin/billing/invoice-accounts', body),
    verifyInvoice: (accountId: string) => api.post<InvoiceAccount>(`/v1/admin/billing/invoice-accounts/${accountId}/verify`),
    withdrawInvoice: (accountId: string) => api.delete<void>(`/v1/admin/billing/invoice-accounts/${accountId}`),
    setInvoiceService: (body: SetInvoiceServiceBody) => api.put<void>('/v1/admin/billing/invoice-service', body),
    invoiceOffers: () => api.get<InvoiceOffers>('/v1/admin/invoice-offers'),
    setInvoiceOffer: (providerKey: string, offered: boolean) => api.put<void>(`/v1/admin/invoice-offers/${providerKey}`, { offered }),
    issueOpenInvoices: () => api.post<IssuedOpenInvoices>('/v1/admin/billing/invoices/issue-open'),
    retryChargeInvoice: (chargeId: string) => api.post<void>(`/v1/admin/billing/charges/${chargeId}/invoice/retry`),
    recordChargeInvoiceSerial: (chargeId: string, serial: string) =>
        api.post<void>(`/v1/admin/billing/charges/${chargeId}/invoice/serial`, { serial }),
    chargeInvoiceLink: (chargeId: string) => api.get<ChargeInvoiceLink>(`/v1/admin/billing/charges/${chargeId}/invoice`),
    chargeAgreement: (chargeId: string) => api.get<ChargeAgreement>(`/v1/admin/billing/charges/${chargeId}/agreement`),

    announcements: () => api.get<Announcements>('/v1/admin/announcements'),
    createAnnouncement: (body: CreateAnnouncementBody) => api.post<Announcements>('/v1/admin/announcements', body),
};
